import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LocationCard, LOCATION_CARD_TABS, type LocationCardTab } from "@/features/locations/LocationCard";
import { getLocation, locationsKeys } from "@/features/locations/locations.api";
import { Skeleton } from "@/components/feedback/Skeleton";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/locations/$id")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "tms_locations") && !hasModule(context.session, "tms_locations_write")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { tab?: LocationCardTab } => ({
    tab: LOCATION_CARD_TABS.find((tab) => tab === search.tab),
  }),
  component: LocationCardScreen,
});

function LocationCardScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const isAdmin = session.roles.includes("admin");
  const canEdit = isAdmin || hasModule(session, "tms_locations_write");

  const query = useQuery({
    queryKey: locationsKeys.detail(id),
    queryFn: () => getLocation(id),
    retry: false,
  });

  if (query.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office Location Card" aria-busy="true">
        <div style={{ marginBottom: "24px" }}>
          <Skeleton width="80px" height="var(--space-3)" />
          <div style={{ marginTop: "10px" }}>
            <Skeleton width="280px" height="var(--space-7)" />
          </div>
        </div>
        <Skeleton width="360px" height="var(--space-6)" />
        <div style={{ marginTop: "20px" }}>
          <Skeleton height="320px" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Location Card">
        <h1 className="t-h2">{t("locations.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("locations.notFoundDesc")}
        </p>
      </div>
    );
  }

  return (
    <div className="wl-page" data-screen-label="Back-office Location Card">
      <LocationCard
        location={query.data}
        canEdit={canEdit}
        isAdmin={isAdmin}
        tab={tab ?? "general"}
        onTab={(next) => navigate({ search: { tab: next === "general" ? undefined : next }, replace: true })}
      />
    </div>
  );
}
